import { IamSetupTabs } from './IamSetupTabs'
import { MANAGED_POLICY_ARNS, READONLY_NAME, waToolCliStep } from './iamSetup'

/**
 * Organization-wide setup help. Two pieces, because one key can't read another
 * account's resources directly:
 *
 *   1. A read-only role (READONLY_NAME) deployed to EVERY member account via a
 *      service-managed CloudFormation StackSet — trusts the management account
 *      and attaches the read-only AWS-managed policies.
 *   2. One access key in the management account, allowed to sts:AssumeRole that
 *      role in any account. The importer assumes it per member account, then
 *      scrapes with the temporary credentials.
 *
 * The role name MUST match backend aws_import_collector.ORG_ASSUME_ROLE_NAME.
 * Single-account setup is the simpler AwsIamSetupHelp.
 */

const ROLE_NAME = READONLY_NAME

// Managed-policy list rendered at the embedded role template's indentation.
const roleManagedDeep = MANAGED_POLICY_ARNS.map((a) => `                - ${a}`).join('\n')

const CLOUDFORMATION = `AWSTemplateFormatVersion: '2010-09-09'
Description: Org-wide read-only role + management-account key for the AI Assessment import
Parameters:
  OrgRootId:
    Type: String
    Description: Organizations root or OU id (r-xxxx / ou-xxxx) to deploy the role into
Resources:
  # 1. Management-account user, allowed to assume the read-only role anywhere in the org.
  ImportUser:
    Type: AWS::IAM::User
    Properties:
      UserName: ${ROLE_NAME}
      Policies:
        - PolicyName: assume-readonly
          PolicyDocument:
            Version: '2012-10-17'
            Statement:
              - Effect: Allow
                Action: sts:AssumeRole
                Resource: arn:aws:iam::*:role/${ROLE_NAME}
  ImportAccessKey:
    Type: AWS::IAM::AccessKey
    Properties:
      UserName: !Ref ImportUser
  # 2. StackSet that creates the read-only role in every member account.
  ReadOnlyRoleStackSet:
    Type: AWS::CloudFormation::StackSet
    Properties:
      StackSetName: ${ROLE_NAME}-role
      PermissionModel: SERVICE_MANAGED
      AutoDeployment:
        Enabled: true
        RetainStacksOnAccountRemoval: false
      Capabilities:
        - CAPABILITY_NAMED_IAM
      Parameters:
        - ParameterKey: ManagementAccountId
          ParameterValue: !Ref AWS::AccountId
      StackInstancesGroup:
        - DeploymentTargets:
            OrganizationalUnitIds:
              - !Ref OrgRootId
          Regions:
            - !Ref AWS::Region
      TemplateBody: |
        AWSTemplateFormatVersion: '2010-09-09'
        Parameters:
          ManagementAccountId:
            Type: String
        Resources:
          ReadOnlyRole:
            Type: AWS::IAM::Role
            Properties:
              RoleName: ${ROLE_NAME}
              AssumeRolePolicyDocument:
                Version: '2012-10-17'
                Statement:
                  - Effect: Allow
                    Principal:
                      AWS: !Sub arn:aws:iam::\${ManagementAccountId}:root
                    Action: sts:AssumeRole
              ManagedPolicyArns:
${roleManagedDeep}
Outputs:
  AccessKeyId:
    Value: !Ref ImportAccessKey
  SecretAccessKey:
    Value: !GetAtt ImportAccessKey.SecretAccessKey

# Deploy from the management (or delegated-admin) account:
#   aws cloudformation deploy --template-file ai-assessment-org.yaml \\
#     --stack-name ${ROLE_NAME}-org --capabilities CAPABILITY_NAMED_IAM \\
#     --parameter-overrides OrgRootId=r-xxxx
# Then read the key pair from the stack outputs.`

const TERRAFORM = `# Management-account user that assumes the read-only role in each member account.
resource "aws_iam_user" "import" {
  name = "${ROLE_NAME}"
}

resource "aws_iam_user_policy" "assume" {
  name = "assume-readonly"
  user = aws_iam_user.import.name
  policy = jsonencode({
    Version = "2012-10-17"
    Statement = [{
      Effect   = "Allow"
      Action   = "sts:AssumeRole"
      Resource = "arn:aws:iam::*:role/${ROLE_NAME}"
    }]
  })
}

resource "aws_iam_access_key" "import" {
  user = aws_iam_user.import.name
}

# StackSet that deploys the read-only role to every account under the org root.
# Save the role template from the CloudFormation tab as readonly-role.yaml.
data "aws_caller_identity" "current" {}
variable "org_root_id" { type = string }

resource "aws_cloudformation_stack_set" "readonly_role" {
  name             = "${ROLE_NAME}-role"
  permission_model = "SERVICE_MANAGED"
  capabilities     = ["CAPABILITY_NAMED_IAM"]
  auto_deployment {
    enabled                          = true
    retain_stacks_on_account_removal = false
  }
  parameters    = { ManagementAccountId = data.aws_caller_identity.current.account_id }
  template_body = file("\${path.module}/readonly-role.yaml")
}

resource "aws_cloudformation_stack_set_instance" "org" {
  stack_set_name = aws_cloudformation_stack_set.readonly_role.name
  deployment_targets {
    organizational_unit_ids = [var.org_root_id]
  }
}

output "access_key_id" {
  value = aws_iam_access_key.import.id
}
output "secret_access_key" {
  value     = aws_iam_access_key.import.secret
  sensitive = true
}`

const CLI = `# 1. Management-account user that can assume the read-only role org-wide.
aws iam create-user --user-name ${ROLE_NAME}
aws iam put-user-policy --user-name ${ROLE_NAME} \\
  --policy-name assume-readonly \\
  --policy-document '{
  "Version": "2012-10-17",
  "Statement": [{
    "Effect": "Allow",
    "Action": "sts:AssumeRole",
    "Resource": "arn:aws:iam::*:role/${ROLE_NAME}"
  }]
}'
# Prints AccessKeyId + SecretAccessKey once — copy them now.
aws iam create-access-key --user-name ${ROLE_NAME}

# 2. StackSet that creates the read-only role in every member account.
# Save the role template from the CloudFormation tab as readonly-role.yaml first.
aws cloudformation create-stack-set --stack-set-name ${ROLE_NAME}-role \\
  --permission-model SERVICE_MANAGED \\
  --auto-deployment Enabled=true,RetainStacksOnAccountRemoval=false \\
  --capabilities CAPABILITY_NAMED_IAM \\
  --template-body file://readonly-role.yaml
aws cloudformation create-stack-instances --stack-set-name ${ROLE_NAME}-role \\
  --deployment-targets OrganizationalUnitIds=r-xxxx \\
  --regions us-east-1

# 3. OPTIONAL (Well-Architected Review): the WA Tool write runs as the member
# role, so run this IN THE ACCOUNT THAT HOSTS THE REVIEWED WORKLOAD, not here.
${waToolCliStep('role', ROLE_NAME, false)}`

const CLOUDSHELL = `# Open AWS CloudShell from the console toolbar (>_ icon, top-right) while signed
# in to the management (or delegated-admin) account — it's pre-authenticated.
# Then paste the same commands as the AWS CLI tab:

${CLI}`

const SNIPPETS = {
  cloudformation: CLOUDFORMATION,
  terraform: TERRAFORM,
  cli: CLI,
  cloudshell: CLOUDSHELL,
}

export function AwsOrgIamSetupHelp() {
  return (
    <IamSetupTabs
      testIdPrefix="aws-org-iam-help"
      triggerLabel="How do I set up org-wide access? (assume-role across accounts)"
      modalTitle="Set up org-wide read-only access"
      snippets={SNIPPETS}
      intro={
        <>
          Organization mode reaches each member account by assuming a read-only role there. Deploy a{' '}
          <code>{ROLE_NAME}</code> role to every account via a service-managed{' '}
          <strong>StackSet</strong> (trusting the management account, with{' '}
          <code>ReadOnlyAccess</code> + <code>AWSBillingReadOnlyAccess</code> attached), plus one
          access key in the management account that can <code>sts:AssumeRole</code> into it. Run this
          from the management or delegated-admin account. Pick your tool:
        </>
      }
      footer={
        <>
          Paste the management-account <strong>Access key ID</strong> +{' '}
          <strong>Secret access key</strong> into the org credential fields, and list the member
          account IDs below. The importer assumes <code>{ROLE_NAME}</code> in each and scrapes
          read-only with the temporary credentials.
        </>
      }
    />
  )
}
